import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, AlertTriangle, Clock, Loader2,
  Copy, Mail, Phone, MapPin, Package, ClipboardCheck, Award, Truck,
  Ban, RotateCcw, FileVideo,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * P3.C · Full-page Certificate Order workflow.
 *
 * Single source of truth for admin actions is the backend's
 * `derive_operational_stage` output (see
 * `backend/certificate_stage.py`). We render — we never invent
 * transitions. Every button on this page maps 1:1 to an entry in
 * `order.admin_actions[]`.
 */

// Ordered stepper — matches the six-step visual workflow the owner
// asked for. Any backend stage that lands in the same "column"
// maps to one of these visual stages.
const STEPS = [
  { key: "order",         label: "Order Received", icon: Package,
    matches: () => true },
  { key: "payment",       label: "Payment", icon: ClipboardCheck,
    matches: (o) => o.payment_status === "verified" },
  { key: "verification",  label: "Verification", icon: FileVideo,
    matches: (o) => o.verification_status === "approved" },
  { key: "certificate",   label: "Certificate", icon: Award,
    matches: (o) => !!(o.final_certificate_number || "").trim() },
  { key: "fulfillment",   label: "Fulfillment", icon: Truck,
    matches: (o) => o.fulfillment_status === "shipped"
                    || o.fulfillment_status === "completed" },
  { key: "completed",     label: "Completed", icon: CheckCircle2,
    matches: (o) => o.fulfillment_status === "completed" },
];

function stepStatus(step, order) {
  if (!order) return "not_started";
  if (step.matches(order)) return "complete";
  // Active step = the first non-complete step given the order's
  // linear progression.
  const idx = STEPS.findIndex((s) => s.key === step.key);
  const prevAllDone = STEPS.slice(0, idx).every((s) => s.matches(order));
  if (!prevAllDone) return "not_started";
  const stage = order.operational_stage;
  // "waiting on customer" stages get a special hourglass state so the
  // owner can see something is truly external.
  if (order.waiting_on === "customer"
      && ((step.key === "payment"      && (order.payment_status !== "verified")
                                        && order.payment_status !== "payment_needs_review")
       || (step.key === "verification" && (order.verification_status !== "approved")
                                        && !["video_received", "video_under_review"].includes(order.verification_status)))) {
    return "waiting_customer";
  }
  // Awaiting-certificate-assignment sits between verification and
  // fulfillment, coloured as "needs admin action".
  if (step.key === "certificate" && stage === "awaiting_certificate_assignment") {
    return "needs_action";
  }
  return "needs_action";
}

const HISTORY_KIND_LABELS = {
  payment_manually_acknowledged: "Payment manually marked received",
  payment_verified:              "Payment verified",
  payment_rejected:              "Payment rejected",
  payment_refunded:              "Payment refunded",
  video_manually_acknowledged:   "Verification video manually marked received",
  video_under_review:            "Verification video moved to Under Review",
  video_approved:                "Verification video approved",
  new_video_requested:           "New verification video requested",
  video_rejected:                "Verification video rejected",
  certificate_assigned:          "Certificate assigned",
  certificate_assignment_rolled_back: "Certificate assignment rolled back (race conflict)",
  preparing:                     "Preparing certificate",
  ready_to_ship:                 "Marked ready to ship",
  order_shipped:                 "Order shipped",
  order_completed:               "Order completed",
  order_cancelled:               "Order cancelled",
  fulfillment_status_auto_advanced: "Fulfillment auto-advanced to Preparing",
};

function humanKind(kind) {
  return HISTORY_KIND_LABELS[kind] || kind.replace(/_/g, " ");
}

function money(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  return (n / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function AdminCertificateOrderDetail() {
  const { publicOrderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyActionId, setBusyActionId] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/certificate-orders/${encodeURIComponent(publicOrderNumber)}`,
        { withCredentials: true });
      setOrder(r.data?.order || null);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) toast.error("Certificate Order not found.");
      else toast.error("Failed to load Certificate Order.");
      setOrder(null);
    } finally { setLoading(false); }
  }, [publicOrderNumber]);

  useEffect(() => { load(); }, [load]);

  const invokeAction = useCallback(async (action) => {
    if (!action || !order) return;
    if (action.confirmation_required && !window.confirm(
      `Confirm: ${action.label}?`
    )) return;
    setBusyActionId(action.id);
    try {
      const url = (action.endpoint_hint || "")
        .replace("PATCH:", "").replace("POST:", "")
        .replace("{public_id}", encodeURIComponent(order.public_order_number));
      const method = (action.http_method || "PATCH").toLowerCase();
      const body = action.patch || {};
      const r = await axios({ url: `${process.env.REACT_APP_BACKEND_URL}${url}`,
        method, data: body, withCredentials: true });
      setOrder(r.data?.order || null);
      toast.success(`${action.label} · done.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (d && (d.message || d.code)) || `${action.label} failed.`;
      toast.error(msg);
    } finally { setBusyActionId(null); }
  }, [order]);

  if (loading && !order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }
  if (!order) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg font-bold mb-2">Order not found.</div>
        <button onClick={() => navigate("/admin/certificate-orders")}
                className="text-[var(--panini-red)] underline font-bold" data-testid="co-back-empty">
          Back to Certificate Orders
        </button>
      </div>
    );
  }

  const primaryAction = order.admin_actions?.[0] || null;
  const secondaryActions = (order.admin_actions || []).slice(1);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="admin-co-detail">
      {/* ── Back nav ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => navigate("/admin/certificate-orders")}
        className="inline-flex items-center gap-2 text-sm font-bold text-[var(--ink)] hover:text-[var(--panini-red)] mb-4"
        data-testid="co-back-to-list"
      >
        <ArrowLeft size={16} /> Back to Certificate Orders
      </button>

      {/* ── Header ───────────────────────────────────────── */}
      <header className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 md:p-6 mb-5" data-testid="co-detail-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
              Certificate Order
            </div>
            <h1 className="text-2xl md:text-3xl font-black" data-testid="co-detail-public-id">
              {order.public_order_number}
            </h1>
            <div className="text-sm mt-1 font-semibold" data-testid="co-detail-customer-name">
              {(order.customer?.first_name || "")} {(order.customer?.last_name || "")}
            </div>
            <div className="text-xs text-[var(--smoke)] mt-1">
              Placed {formatDT(order.created_at)}
              {" · "}
              {(order.album?.cover_type || "").replace(/_/g, " ") || "cover unknown"}
              {" · "}
              qty {order.quantity || 1}
            </div>
          </div>
          <StageBadge stage={order.operational_stage}
                       label={order.operational_stage_label}
                       waitingOn={order.waiting_on} />
        </div>
      </header>

      {/* ── Big NEXT ACTION card ─────────────────────────── */}
      <NextActionCard
        primary={primaryAction}
        secondary={secondaryActions}
        busyId={busyActionId}
        onInvoke={(a) => (a.id === "assign_certificate"
                             ? setAssignOpen(true)
                             : invokeAction(a))}
        waitingOn={order.waiting_on}
        stage={order.operational_stage}
      />

      {/* ── Workflow Stepper ─────────────────────────────── */}
      <Stepper order={order} />

      {/* ── Panels ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <CustomerPanel order={order} />
        <PricingPanel order={order} />
        <PaymentPanel order={order} onInvoke={invokeAction} busyId={busyActionId} />
        <VerificationPanel order={order} onInvoke={invokeAction} busyId={busyActionId} />
        <CertificatePanel order={order} onAssign={() => setAssignOpen(true)} />
        <FulfillmentPanel order={order} onInvoke={invokeAction} busyId={busyActionId} />
      </div>

      <ActivityTimeline history={order.history || []} />

      {assignOpen && (
        <AssignCertificateModal
          order={order}
          onClose={() => setAssignOpen(false)}
          onAssigned={(fresh) => {
            setAssignOpen(false);
            setOrder(fresh);
            toast.success(`Assigned ${fresh.final_certificate_number}`);
          }}
        />
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function StageBadge({ stage, label, waitingOn }) {
  const tone = {
    awaiting_payment:                "bg-amber-100 text-amber-900 border-amber-700",
    payment_needs_review:            "bg-amber-100 text-amber-900 border-amber-700",
    awaiting_video:                  "bg-amber-100 text-amber-900 border-amber-700",
    video_needs_review:              "bg-amber-100 text-amber-900 border-amber-700",
    new_video_required:              "bg-red-100 text-red-900 border-red-700",
    awaiting_certificate_assignment: "bg-purple-100 text-purple-900 border-purple-700",
    preparing:                       "bg-sky-100 text-sky-900 border-sky-700",
    ready_to_ship:                   "bg-sky-100 text-sky-900 border-sky-700",
    shipped:                         "bg-emerald-100 text-emerald-900 border-emerald-700",
    completed:                       "bg-emerald-100 text-emerald-900 border-emerald-700",
    cancelled:                       "bg-neutral-200 text-neutral-800 border-neutral-500",
  }[stage] || "bg-slate-100 text-slate-800 border-slate-500";
  return (
    <div className="text-right">
      <div className={"inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest border-2 " + tone}
           data-testid={`co-stage-badge-${stage}`}>
        {label || stage}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
        {waitingOn === "customer" ? "Waiting on customer"
          : waitingOn === "admin" ? "Waiting on admin"
          : ""}
      </div>
    </div>
  );
}

function NextActionCard({ primary, secondary, busyId, onInvoke, waitingOn, stage }) {
  if (!primary) {
    // Truly nothing to do (completed / cancelled).
    return (
      <div className="rounded-xl border-2 border-neutral-300 bg-neutral-50 p-4 md:p-6 mb-5" data-testid="co-next-action-none">
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
          Next Action
        </div>
        <div className="text-lg md:text-xl font-black">
          {stage === "completed" ? "Order Complete." : "No admin action available."}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-4 border-[var(--panini-red)] bg-white p-4 md:p-6 mb-5 shadow-md" data-testid="co-next-action-card">
      <div className="text-[10px] uppercase tracking-widest text-[var(--panini-red)] font-black">
        Next Action
      </div>
      <div className="text-xl md:text-2xl font-black uppercase tracking-wide" data-testid="co-next-action-label">
        {primary.label}
      </div>
      {waitingOn === "customer" && (
        <div className="mt-1 text-xs text-[var(--smoke)] italic">
          System still shows Waiting on Customer — use this action if the customer already sent it externally (Zelle, WhatsApp, etc.).
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          onClick={() => onInvoke(primary)}
          disabled={busyId === primary.id}
          className="px-4 py-2 rounded-lg text-sm font-black uppercase tracking-widest bg-[var(--panini-red)] text-white border-2 border-[var(--ink)] hover:brightness-110 disabled:opacity-50"
          data-testid={`co-action-primary-${primary.id}`}
        >
          {busyId === primary.id ? "Working…" : primary.label}
        </button>
        {secondary.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onInvoke(a)}
            disabled={busyId === a.id}
            className={"px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest border-2 disabled:opacity-50 " +
              (a.dangerous
                ? "bg-white text-red-800 border-red-700 hover:bg-red-50"
                : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-neutral-100")}
            data-testid={`co-action-secondary-${a.id}`}
          >
            {busyId === a.id ? "…" : a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Stepper({ order }) {
  return (
    <div className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 mb-5" data-testid="co-stepper">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {STEPS.map((step) => {
          const st = stepStatus(step, order);
          const Icon = step.icon;
          const tone = {
            complete:         "bg-emerald-100 text-emerald-900 border-emerald-700",
            needs_action:     "bg-amber-100 text-amber-900 border-amber-700",
            waiting_customer: "bg-sky-100 text-sky-900 border-sky-700",
            not_started:     "bg-neutral-100 text-neutral-500 border-neutral-300",
          }[st];
          const icon = {
            complete:         <CheckCircle2 size={16} />,
            needs_action:     <AlertTriangle size={16} />,
            waiting_customer: <Clock size={16} />,
            not_started:     <Circle size={16} />,
          }[st];
          return (
            <div key={step.key}
                 className={"rounded-lg border-2 px-3 py-2 flex items-center gap-2 " + tone}
                 data-testid={`co-step-${step.key}-${st}`}>
              <Icon size={16} />
              <div className="text-[11px] font-black uppercase tracking-widest truncate">{step.label}</div>
              <div className="ml-auto">{icon}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Panel({ title, children, testid, icon: Icon }) {
  return (
    <section className="border-2 border-[var(--ink)] rounded-xl bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-[var(--panini-red)]" />}
        <h2 className="text-xs font-black uppercase tracking-widest">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function CustomerPanel({ order }) {
  const c = order.customer || {};
  const sa = order.shipping_address || {};
  return (
    <Panel title="Customer" testid="co-customer-panel" icon={Package}>
      <div className="text-sm font-bold" data-testid="co-customer-fullname">
        {c.first_name} {c.last_name}
      </div>
      <div className="text-xs text-[var(--smoke)] mt-1 flex items-center gap-1">
        <Mail size={12} /> {c.email}
      </div>
      <div className="text-xs text-[var(--smoke)] mt-0.5 flex items-center gap-1">
        <Phone size={12} /> {c.phone}
      </div>
      <div className="text-xs text-[var(--smoke)] mt-2 flex items-start gap-1">
        <MapPin size={12} className="mt-0.5" />
        <div>
          {sa.line1 || ""}
          {sa.line2 ? `, ${sa.line2}` : ""}
          <br />
          {[sa.city, sa.state, sa.postal_code].filter(Boolean).join(", ")}
          {sa.country ? `, ${sa.country}` : ""}
        </div>
      </div>
    </Panel>
  );
}

function PricingPanel({ order }) {
  const p = order.pricing || {};
  return (
    <Panel title="Order" testid="co-pricing-panel">
      <div className="grid grid-cols-2 gap-y-1 text-sm">
        <div className="text-[var(--smoke)]">Certificate type</div>
        <div className="font-bold text-right">{(order.album?.cover_type || "—").replace(/_/g, " ")}</div>
        <div className="text-[var(--smoke)]">Quantity</div>
        <div className="font-bold text-right">{order.quantity || 1}</div>
        <div className="text-[var(--smoke)]">Amount</div>
        <div className="font-mono font-bold text-right" data-testid="co-total">{money(p.total_cents)}</div>
      </div>
    </Panel>
  );
}

function StatusChip({ tone, children, testid }) {
  const cls = {
    green:  "bg-emerald-100 text-emerald-900 border-emerald-700",
    yellow: "bg-amber-100 text-amber-900 border-amber-700",
    red:    "bg-red-100 text-red-900 border-red-700",
    blue:   "bg-sky-100 text-sky-900 border-sky-700",
    grey:   "bg-neutral-100 text-neutral-800 border-neutral-400",
  }[tone] || "bg-neutral-100 text-neutral-800 border-neutral-400";
  return (
    <span className={"inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border " + cls}
          data-testid={testid}>
      {children}
    </span>
  );
}

function PaymentPanel({ order, onInvoke, busyId }) {
  const p = order.payment_status;
  const tone = p === "verified" ? "green"
    : p === "payment_needs_review" ? "yellow"
    : p === "failed" || p === "refunded" ? "red"
    : "grey";
  const label = ({
    awaiting_payment:     "Awaiting Payment",
    payment_submitted:    "Payment Submitted",
    payment_needs_review: "Payment Needs Review",
    verified:             "Verified",
    failed:               "Failed",
    refunded:             "Refunded",
  }[p] || p);
  const actions = (order.admin_actions || []).filter(
    (a) => a.id === "mark_payment_received" || a.id === "verify_payment" || a.id === "reject_payment");
  return (
    <Panel title="Payment" testid="co-payment-panel" icon={ClipboardCheck}>
      <div className="flex items-center justify-between mb-2">
        <StatusChip tone={tone} testid={`co-payment-status-${p}`}>{label}</StatusChip>
        <div className="text-xs font-mono">{money((order.pricing || {}).total_cents)}</div>
      </div>
      {actions.length === 0 && (
        <div className="text-xs text-[var(--smoke)] italic">No payment actions available at this stage.</div>
      )}
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onInvoke(a)}
            disabled={busyId === a.id}
            className={"px-2.5 py-1.5 rounded text-[11px] font-bold uppercase tracking-widest border-2 disabled:opacity-50 " +
              (a.dangerous
                ? "bg-white text-red-800 border-red-700 hover:bg-red-50"
                : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-neutral-100")}
            data-testid={`co-payment-action-${a.id}`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </Panel>
  );
}

function VerificationPanel({ order, onInvoke, busyId }) {
  const v = order.verification_status;
  const tone = v === "approved" ? "green"
    : (v === "video_received" || v === "video_under_review") ? "yellow"
    : (v === "rejected" || v === "new_video_required") ? "red"
    : "grey";
  const label = ({
    video_pending:      "Video Pending",
    video_received:     "Video Received",
    video_under_review: "Under Review",
    approved:           "Approved",
    new_video_required: "New Video Required",
    rejected:           "Rejected",
  }[v] || v);
  const actions = (order.admin_actions || []).filter(
    (a) => a.id === "mark_video_received" || a.id === "approve_video"
        || a.id === "request_new_video" || a.id === "reject_video");
  return (
    <Panel title="Verification" testid="co-verification-panel" icon={FileVideo}>
      <StatusChip tone={tone} testid={`co-verification-status-${v}`}>{label}</StatusChip>
      <div className="mt-2 flex flex-wrap gap-2">
        {actions.length === 0 && (
          <div className="text-xs text-[var(--smoke)] italic">No verification actions available at this stage.</div>
        )}
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onInvoke(a)}
            disabled={busyId === a.id}
            className={"px-2.5 py-1.5 rounded text-[11px] font-bold uppercase tracking-widest border-2 disabled:opacity-50 " +
              (a.dangerous
                ? "bg-white text-red-800 border-red-700 hover:bg-red-50"
                : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-neutral-100")}
            data-testid={`co-verification-action-${a.id}`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </Panel>
  );
}

function CertificatePanel({ order, onAssign }) {
  const cn = (order.final_certificate_number || "").trim();
  const assignAction = (order.admin_actions || []).find((a) => a.id === "assign_certificate");
  return (
    <Panel title="Certificate" testid="co-certificate-panel" icon={Award}>
      {cn ? (
        <div>
          <div className="text-xs text-[var(--smoke)] uppercase tracking-widest font-bold">Assigned</div>
          <div className="text-2xl font-black font-mono" data-testid="co-certificate-number">{cn}</div>
          <div className="mt-1 text-xs text-[var(--smoke)]">
            {order.final_certificate_assigned_at
              ? `${formatDT(order.final_certificate_assigned_at)} by ${order.final_certificate_assigned_by || "admin"}`
              : ""}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { navigator.clipboard?.writeText(cn); toast.success("Certificate number copied."); }}
              className="inline-flex items-center gap-1 text-xs font-bold text-[var(--panini-red)] hover:underline"
              data-testid="co-copy-cert"
            >
              <Copy size={12} /> Copy
            </button>
            <span className="text-[10px] italic text-[var(--smoke)]">
              Registry record not yet created — issue separately from Admin → Registry.
            </span>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm text-[var(--smoke)]">Not yet assigned.</div>
          {assignAction && (
            <button
              type="button"
              onClick={onAssign}
              className="mt-3 px-3 py-2 rounded text-xs font-black uppercase tracking-widest bg-[var(--panini-red)] text-white border-2 border-[var(--ink)] hover:brightness-110"
              data-testid="co-open-assign-modal"
            >
              Assign Certificate
            </button>
          )}
        </div>
      )}
    </Panel>
  );
}

function FulfillmentPanel({ order, onInvoke, busyId }) {
  const f = order.fulfillment_status;
  const tone = f === "completed" || f === "shipped" ? "green"
    : (f === "preparing_certificate" || f === "ready_to_ship") ? "blue"
    : f === "cancelled" ? "red"
    : "grey";
  const label = ({
    waiting_for_verification: "Waiting for Verification",
    preparing_certificate:    "Preparing",
    ready_to_ship:            "Ready to Ship",
    shipped:                  "Shipped",
    completed:                "Completed",
    cancelled:                "Cancelled",
  }[f] || f);
  const actions = (order.admin_actions || []).filter(
    (a) => a.id === "mark_ready_to_ship" || a.id === "mark_shipped" || a.id === "mark_completed");
  return (
    <Panel title="Fulfillment" testid="co-fulfillment-panel" icon={Truck}>
      <div className="flex items-center justify-between mb-2">
        <StatusChip tone={tone} testid={`co-fulfillment-status-${f}`}>{label}</StatusChip>
        {order.shipped_at && (
          <div className="text-[10px] text-[var(--smoke)]">Shipped {formatDT(order.shipped_at)}</div>
        )}
      </div>
      <div className="text-xs text-[var(--smoke)] mb-2">
        USPS Stamp Mail — no tracking number is required for Certificate Orders.
      </div>
      <div className="flex flex-wrap gap-2">
        {actions.length === 0 && (
          <div className="text-xs text-[var(--smoke)] italic">
            {f === "completed" ? "Order complete." : "No fulfillment actions available."}
          </div>
        )}
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onInvoke(a)}
            disabled={busyId === a.id}
            className={"px-2.5 py-1.5 rounded text-[11px] font-bold uppercase tracking-widest border-2 disabled:opacity-50 " +
              (a.dangerous
                ? "bg-white text-red-800 border-red-700 hover:bg-red-50"
                : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-neutral-100")}
            data-testid={`co-fulfillment-action-${a.id}`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </Panel>
  );
}

function ActivityTimeline({ history }) {
  const sorted = useMemo(
    () => (Array.isArray(history) ? [...history] : [])
      .filter((h) => h && h.at)
      .sort((a, b) => (b.at || "").localeCompare(a.at || "")),
    [history]);
  if (sorted.length === 0) return null;
  return (
    <section className="border-2 border-[var(--ink)] rounded-xl bg-white p-4 mt-6" data-testid="co-timeline">
      <h2 className="text-xs font-black uppercase tracking-widest mb-3">Activity Timeline</h2>
      <ul className="space-y-2">
        {sorted.map((h, i) => (
          <li key={i} className="flex gap-3 text-sm">
            <div className="w-40 flex-shrink-0 text-xs text-[var(--smoke)] font-mono">{formatDT(h.at)}</div>
            <div className="flex-1">
              <div className="font-bold">{humanKind(h.kind)}</div>
              {h.actor && (
                <div className="text-[10px] text-[var(--smoke)]">by {h.actor} {h.source ? `· ${h.source.replace(/_/g, " ")}` : ""}</div>
              )}
              {h.detail?.reason && (
                <div className="text-xs italic mt-0.5">{h.detail.reason}</div>
              )}
              {h.detail?.certificate_number && (
                <div className="text-xs font-mono mt-0.5">{h.detail.certificate_number}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AssignCertificateModal({ order, onClose, onAssigned }) {
  const orderCoverType = (order.album?.cover_type || "").toLowerCase();
  const inferred = /(^|_|\b)hard/.test(orderCoverType) ? "HC"
    : /(^|_|\b)soft/.test(orderCoverType) ? "SC" : "HC";
  const [albumType, setAlbumType] = useState(inferred);
  const [seq, setSeq] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { album_type: albumType };
      if (seq.trim()) {
        const n = parseInt(seq.trim(), 10);
        if (!Number.isFinite(n) || n < 1) throw new Error("Invalid sequence number");
        body.sequence = n;
      }
      const r = await axios.post(
        `${API}/admin/certificate-orders/${encodeURIComponent(order.public_order_number)}/assign-certificate`,
        body, { withCredentials: true });
      onAssigned(r.data?.order);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (d && (d.message || d.code)) || "Assignment failed.";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" data-testid="co-assign-modal">
      <div className="bg-white border-4 border-[var(--ink)] rounded-xl p-5 w-full max-w-md">
        <div className="flex items-center gap-2 mb-3">
          <Award size={18} className="text-[var(--panini-red)]" />
          <h2 className="text-lg font-black uppercase tracking-widest">Assign Certificate</h2>
        </div>
        <label className="block text-xs font-bold uppercase tracking-widest mb-1">Cover</label>
        <select value={albumType} onChange={(e) => setAlbumType(e.target.value)}
                className="w-full border-2 border-[var(--ink)] rounded px-3 py-2 mb-3 font-bold"
                data-testid="co-assign-album-type">
          <option value="HC">HC — Hard Cover</option>
          <option value="SC">SC — Soft Cover</option>
        </select>
        <label className="block text-xs font-bold uppercase tracking-widest mb-1">Sequence (optional)</label>
        <input
          type="number"
          value={seq}
          onChange={(e) => setSeq(e.target.value)}
          placeholder="Auto-pick next available"
          className="w-full border-2 border-[var(--ink)] rounded px-3 py-2 mb-1 font-mono"
          data-testid="co-assign-sequence"
        />
        <div className="text-[10px] text-[var(--smoke)] mb-4">
          Leave blank to auto-pick the lowest available serial (registry + reservations).
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded text-xs font-bold uppercase tracking-widest bg-white text-[var(--ink)] border-2 border-[var(--ink)]"
            data-testid="co-assign-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded text-xs font-black uppercase tracking-widest bg-[var(--panini-red)] text-white border-2 border-[var(--ink)] disabled:opacity-50"
            data-testid="co-assign-submit"
          >
            {busy ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
